import { action, makeObservable, observable } from 'mobx';

import { isAfter, isBefore, startOfToday } from 'date-fns';
import ApiService from '../services/ApiService';
import { invoiceIconName } from '../util/FormatUtil';
import { Invoice } from './models/Invoice';
import { InvoiceFilter } from './models/InvoiceFilter';
import { LoadableArray } from './models/LoadableArray';
import { ISsoStore } from './SsoStore';
import { ITranslateStore } from './TranslateStore';
import { ContentType } from './models/ContentType';

export interface IInvoicesStore {
  readonly previousInvoices: LoadableArray<Invoice>;
  readonly previousInvoicesFilter: InvoiceFilter;
  readonly defaultPrepaymentAmount: number;

  loadPreviousInvoices(filter: InvoiceFilter): void;

  loadMorePreviousInvoices(): void;

  overdue(invoice: Invoice): boolean;

  defaultInvoiceName(invoiceType: ContentType): string;

  invoiceName(invoice: Invoice): string;

  refNumberName(invoiceType: ContentType, assignedName: string, restricted: boolean, source: string): string;

  sortIssueDateAscending(first: Invoice, second: Invoice): number;
}

export default class InvoicesStore implements IInvoicesStore {
  @observable
  public previousInvoices: LoadableArray<Invoice> = new LoadableArray<Invoice>();

  public defaultPrepaymentAmount = 30.0;
  public previousInvoicesFilter: InvoiceFilter;
  private static PREVIOUS_INVOICES_PAGE_SIZE = 50;

  constructor(
    private ssoStore: ISsoStore,
    private apiService: ApiService,
    private translateStore: ITranslateStore
  ) {
    makeObservable(this);
  }

  @action
  public async loadPreviousInvoices(filter: InvoiceFilter): Promise<void> {
    if (this.previousInvoices.isLoading) {
      return;
    }

    if (!this.shouldReloadPreviousInvoices(filter)) {
      return;
    }

    filter.offset = 0;
    filter.limit = InvoicesStore.PREVIOUS_INVOICES_PAGE_SIZE;
    this.previousInvoicesFilter = filter;

    this.previousInvoices = new LoadableArray<Invoice>();

    await this.loadMorePreviousInvoices();
  }

  @action
  public async loadMorePreviousInvoices(): Promise<any> {
    if (this.previousInvoices.isLoading) {
      return;
    }

    const previousInvoicesPromise = this.apiService.getPreviousInvoices(this.ssoStore.customer.customerId, this.previousInvoicesFilter);

    const transform = (result: Invoice[]) => {
      this.previousInvoicesFilter.offset += InvoicesStore.PREVIOUS_INVOICES_PAGE_SIZE;
      return this.transformInvoices(result);
    };

    const transformedPreviousInvoices = LoadableArray.toLoadableArray<Invoice, Invoice>(previousInvoicesPromise, transform);

    this.previousInvoices.loadMore(transformedPreviousInvoices.promise, (result) => {
      return result.length < InvoicesStore.PREVIOUS_INVOICES_PAGE_SIZE;
    });

    await transformedPreviousInvoices.promise;
  }

  public overdue(invoice: Invoice): boolean {
    return invoice.unsettledAmount > 0 && isBefore(new Date(invoice.dueDate), startOfToday());
  }

  private transformInvoices(invoices: Invoice[]): Invoice[] {
    invoices.forEach((invoice) => {
      invoice.iconName = invoiceIconName(invoice.contentType);
    });
    return invoices;
  }

  public invoiceName(invoice: Invoice): string {
    if (invoice.restricted) {
      return invoice.assignedName;
    }
    return this.refNumberName(invoice.contentType, invoice.assignedName, invoice.restricted, invoice.source);
  }

  public refNumberName(invoiceType: ContentType, assignedName: string, restricted: boolean, source: string): string {
    const alwaysShowDefaultName = source === 'TBCIS' && invoiceType === 'MOBILE' && !restricted;
    if (alwaysShowDefaultName) {
      return this.defaultInvoiceName(invoiceType);
    }
    if (assignedName != null) {
      return assignedName;
    }
    return this.defaultInvoiceName(invoiceType);
  }

  public sortIssueDateAscending(first: Invoice, second: Invoice): number {
    if (isAfter(new Date(first.issuedDate), new Date(second.issuedDate))) {
      return 1;
    }
    if (isBefore(new Date(first.issuedDate), new Date(second.issuedDate))) {
      return -1;
    }
    return 0;
  }

  public defaultInvoiceName(invoiceType: ContentType): string {
    const translate = this.translateStore.translate;
    switch (invoiceType) {
      case 'MOBILE':
        return translate('shared.invoices.invoice-name.mobile-service');
      case 'GOODS':
        return translate('shared.invoices.invoice-name.product');
      case 'STATE_FEES':
        return translate('shared.invoices.invoice-name.state-fees');
      case 'FINANCE':
        return translate('shared.invoices.invoice-name.finance');
      case 'DEBT':
        return translate('shared.invoices.invoice-name.debt');
      case 'TELESERVICES':
        return translate('shared.invoices.invoice-name.teleservices');
      case 'PENALTY':
        return translate('shared.invoices.invoice-name.penalty');
      default:
        return translate('shared.invoices.invoice-name.other');
    }
  }

  private shouldReloadPreviousInvoices(newFilter: InvoiceFilter) {
    const previousFilter = this.previousInvoicesFilter;
    if (previousFilter == null) {
      return true;
    }
    if (previousFilter.fromDate !== newFilter.fromDate) {
      return true;
    }
    return previousFilter.toDate !== newFilter.toDate;
  }
}
